// SOS mecânica: o motorista diz o problema, o app pega o local (GPS do
// celular) e mostra as oficinas de confiança perto e as do Google Maps, num
// raio de 20 km (dá para ampliar para 50 e 100 km), com Ligar, WhatsApp e
// Rota. O motorista avisa o titular pelo WhatsApp, com o problema e o local.
// Sem internet, aparecem só as oficinas de confiança (estão no celular).
import { colaboradores, eventosDaViagem, oficinas, veiculos, viagens } from '../dados.js';
import { h, icone, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { oficinasNoGoogle, temGoogle } from '../lib/google.js';
import {
  RAIOS_KM, TIPOS_DE_PROBLEMA, ehCelular, linkDaRota, linkDeLigar, linkDoLocal, linkDoWhatsApp, mensagemDoSos, oficinasDeConfianca,
  semAsDeConfianca, telefoneNaTela, textoDistancia, tipoDoProblema,
} from '../lib/sos.js';
import { estado as sinal } from '../local/sincronia.js';
import { ehMotorista, motoristaGrava, podeGravar, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, carregando, emblema, faixa, secao } from '../ui/componentes.js';
import { nomeCurto, textoCaminhao } from './viagens.js';

function localDoCelular() {
  return new Promise((ok, erro) => {
    if (!navigator.geolocation) {
      erro(Object.assign(new Error('sem gps'), { codigo: 0 }));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => ok({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      (e) => erro(Object.assign(new Error(e.message), { codigo: e.code })),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 });
  });
}

function textoDoErroDeLocal(erro) {
  if (erro.codigo === 1) {
    return 'O app não tem permissão para ver onde você está. No Chrome, toque no cadeado ao lado do endereço (ou nos três pontinhos → Configurações → Configurações do site → Local), permita e toque em "Tentar de novo".';
  }
  if (erro.codigo === 0) return 'Este aparelho não informa a localização.';
  return 'Não deu para pegar a localização. Confira se a Localização (GPS) do celular está ligada e toque em "Tentar de novo".';
}

// Ligar, WhatsApp e Rota de uma oficina.
function botoesDaOficina(o) {
  return h('div', { class: 'acoes-linha botoes-oficina' },
    o.telefone && h('a', { class: 'botao', href: linkDeLigar(o.telefone) }, icone('telefone', { tamanho: 20 }), 'Ligar'),
    o.telefone && (o.whatsapp || ehCelular(o.telefone)) &&
      h('a', { class: 'botao secundario', href: linkDoWhatsApp(o.telefone), target: '_blank', rel: 'noopener' }, 'WhatsApp'),
    o.latitude != null && h('a', { class: 'botao secundario', href: linkDaRota(o), target: '_blank', rel: 'noopener' }, icone('rota', { tamanho: 20 }), 'Rota'));
}

function cartaoDaOficina(o, { deConfianca, aoGuardar = null }) {
  const onde = [o.endereco, [o.cidade, o.uf].filter(Boolean).join('/')].filter(Boolean).join(' · ');
  return h('div', { class: 'cartao oficina' },
    h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, o.nome),
      h('span', { class: 'cartao-linha' }, [o.distancia != null ? textoDistancia(o.distancia) : null, onde || null].filter(Boolean).join(' · ')),
      o.telefone && h('span', { class: 'cartao-linha' }, telefoneNaTela(o.telefone)),
      o.observacao && h('span', { class: 'cartao-linha' }, o.observacao),
      h('span', { class: 'cartao-emblemas' },
        deConfianca && emblema('De confiança', 'ok'),
        o.aberta === true && emblema('Aberta agora', 'ok'),
        o.aberta === false && emblema('Fechada agora', 'alerta')),
      botoesDaOficina(o),
      aoGuardar && h('button', { class: 'botao texto', type: 'button', onClick: (e) => aoGuardar(e.currentTarget) }, 'Guardar nas oficinas de confiança')));
}

export function telaSos({ busca }) {
  const estado = { tipo: tipoDoProblema(busca.get('tipo'))?.id ?? null, local: null, semLocal: false, raio: RAIOS_KM[0] };
  // A tela não é redesenhada quando chegam dados do servidor (perderia o
  // problema escolhido e o local).
  const corpo = h('div', { class: 'secao', dataset: { atualizaSozinha: '1' } });
  let rodada = 0;

  function escolherProblema() {
    trocar(corpo,
      h('h2', { class: 'titulo-sos' }, 'Qual é o problema?'),
      h('div', { class: 'problemas' },
        TIPOS_DE_PROBLEMA.map((t) => h('button', {
          class: 'problema',
          type: 'button',
          onClick: () => {
            estado.tipo = t.id;
            pegarLocal();
          },
        }, h('strong', null, t.nome), h('small', null, t.dica)))),
      !ehMotorista() && h('p', { class: 'pequeno' }, 'É a tela que o motorista vê no app dele. As oficinas de confiança ficam em ',
        h('a', { href: '#/oficinas' }, 'Oficinas de confiança'), '.'));
  }

  async function pegarLocal() {
    const minha = ++rodada;
    trocar(corpo, carregando('Pegando a sua localização…'));
    try {
      estado.local = await localDoCelular();
      estado.semLocal = false;
    } catch (erro) {
      if (minha !== rodada) return;
      trocar(corpo,
        faixa(textoDoErroDeLocal(erro), 'alerta', 'Sem a localização'),
        h('div', { class: 'acoes' },
          h('button', { class: 'botao cheio', type: 'button', onClick: () => pegarLocal() }, 'Tentar de novo'),
          h('button', {
            class: 'botao secundario cheio',
            type: 'button',
            onClick: () => {
              estado.semLocal = true;
              mostrar();
            },
          }, 'Ver as oficinas de confiança assim mesmo'),
          h('button', { class: 'botao texto', type: 'button', onClick: () => escolherProblema() }, 'Escolher outro problema')));
      return;
    }
    if (minha === rodada) mostrar();
  }

  async function mostrar() {
    const minha = ++rodada;
    const tipo = tipoDoProblema(estado.tipo);
    const local = estado.semLocal ? null : estado.local;
    const [lista, frota, minhas, eu] = await Promise.all([
      oficinas.listar(), veiculos.listar(), ehMotorista() ? viagens.listar() : [],
      ehMotorista() && sessao.colaborador ? colaboradores.obter(sessao.colaborador) : null,
    ]);
    if (minha !== rodada) return;
    const confianca = oficinasDeConfianca(lista, { tipo: tipo.id, local, raioKm: estado.raio });
    const podeGuardar = podeGravar() || motoristaGrava();

    // Aviso ao titular (só no app do motorista)
    let aviso = null;
    const celular = sessao.empresa?.celular;
    if (ehMotorista() && celular) {
      const viagem = minhas.find((v) => v.status === 'aberta');
      const caminhao = viagem ? frota.find((v) => v.id === viagem.veiculo_id) : null;
      const texto = mensagemDoSos({ motorista: eu?.nome ?? nomeCurto(eu), caminhao: caminhao ? textoCaminhao(caminhao) : null, tipo: tipo.id, local });
      // Modo viagem (0.23.0): o SOS fica nos horários da viagem em andamento.
      const anotar = () => {
        if (!viagem || !motoristaGrava()) return;
        eventosDaViagem.criar({
          viagem_id: viagem.id, tipo: 'sos', momento: new Date().toISOString(),
          lat: local ? Number(local.latitude.toFixed(6)) : null, lon: local ? Number(local.longitude.toFixed(6)) : null,
          dados: { problema: tipo.id },
        }, { esperarServidor: false }).catch((erro) => console.warn(`SOS: evento da viagem: ${erro?.message ?? erro}`));
      };
      aviso = h('a', { class: 'botao cheio perigo-cheio', href: linkDoWhatsApp(celular, texto), target: '_blank', rel: 'noopener', onClick: anotar },
        `Avisar ${sessao.empresa.nome_fantasia || sessao.empresa.nome} pelo WhatsApp`);
    }

    const raios = h('div', { class: 'raios', role: 'group', 'aria-label': 'Até quantos km' },
      RAIOS_KM.map((km) => h('button', {
        class: `botao ${km === estado.raio ? '' : 'secundario'}`,
        type: 'button',
        'aria-pressed': String(km === estado.raio),
        disabled: !local,
        onClick: () => {
          estado.raio = km;
          mostrar();
        },
      }, `${km} km`)));

    const lugarGoogle = h('div', { class: 'lista' });
    trocar(corpo,
      h('div', { class: 'cartao destaque sos-resumo' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, `${tipo.nome} (${tipo.dica})`),
        h('span', { class: 'cartao-linha' }, local
          ? h('a', { href: linkDoLocal(local), target: '_blank', rel: 'noopener' }, 'Você está aqui (ver no mapa)')
          : 'Sem a localização: as distâncias não aparecem.'),
        h('button', { class: 'botao texto', type: 'button', onClick: () => escolherProblema() }, 'Trocar o problema'))),
      aviso,
      local && raios,
      secao('Oficinas de confiança', h('div', { class: 'lista' },
        local && confianca.perto.length === 0 && h('p', { class: 'pequeno' }, `Nenhuma oficina de confiança até ${estado.raio} km.`),
        confianca.perto.map((o) => cartaoDaOficina(o, { deConfianca: true })),
        confianca.semLocal.length > 0 && h('p', { class: 'pequeno' }, 'Sem o local cadastrado (a distância não aparece):'),
        confianca.semLocal.map((o) => cartaoDaOficina(o, { deConfianca: true }))),
      { contador: String(confianca.perto.length + confianca.semLocal.length) }),
      secao('No Google Maps', lugarGoogle));

    if (!local) {
      trocar(lugarGoogle, h('p', { class: 'pequeno' }, 'A busca no Google Maps precisa da sua localização.'));
      return;
    }
    if (!temGoogle()) {
      trocar(lugarGoogle, h('p', { class: 'pequeno' }, 'A busca no Google Maps ainda não está ligada. Por enquanto, aparecem só as oficinas de confiança.'));
      return;
    }
    if (!sinal.online) {
      trocar(lugarGoogle, faixa('Sem internet agora: aparecem só as oficinas de confiança, que ficam guardadas no celular.', 'alerta'));
      return;
    }
    trocar(lugarGoogle, carregando('Procurando no Google Maps…'));
    try {
      const achadas = semAsDeConfianca(await oficinasNoGoogle({ busca: tipo.busca, local, raioKm: estado.raio }), lista);
      if (minha !== rodada) return;
      const guardar = async (o, botao) => {
        botao.disabled = true;
        try {
          await oficinas.criar({
            nome: o.nome, telefone: o.telefone, whatsapp: ehCelular(o.telefone), endereco: o.endereco,
            latitude: Number(o.latitude.toFixed(6)), longitude: Number(o.longitude.toFixed(6)), tipos: [tipo.id], google_place_id: o.google_place_id,
          });
          avisar('Guardada nas oficinas de confiança.');
          mostrar();
        } catch (erro) {
          botao.disabled = false;
          avisar(mensagemDeErro(erro), 'erro');
        }
      };
      trocar(lugarGoogle,
        achadas.length === 0 && h('p', { class: 'pequeno' },
          estado.raio < RAIOS_KM[RAIOS_KM.length - 1] ? `Nada até ${estado.raio} km. Toque num raio maior.` : `Nada até ${estado.raio} km.`),
        achadas.map((o) => cartaoDaOficina(o, { deConfianca: false, aoGuardar: podeGuardar ? (botao) => guardar(o, botao) : null })),
        achadas.length > 0 && h('p', { class: 'pequeno' }, 'Ligue antes de ir: o app não sabe se a oficina pode atender agora.'));
    } catch (erro) {
      if (minha !== rodada) return;
      trocar(lugarGoogle, faixa(`Não deu para buscar no Google Maps agora. ${mensagemDeErro(erro)}`, 'alerta'));
    }
  }

  if (estado.tipo) pegarLocal();
  else escolherProblema();

  return casca({ titulo: 'SOS mecânica', voltar: ehMotorista() ? null : '/mais', conteudo: corpo });
}
